import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FormData,
  ValidationError,
  validateForm,
  formatPhone,
  cleanPhone,
} from '../../utils/validation';
import { useTracking } from '../../hooks/useTracking';

interface TuMesaYaFormProps {
  onSubmit: (data: FormData) => boolean | void | Promise<boolean | void>;
  isSubmitting?: boolean;
  variant?: 'hero' | 'final' | 'demo' | 'trial';
  className?: string;
  compact?: boolean;
  submitLabel?: string;
}

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-primary);
  font-family: var(--font-primary), var(--font-secondary), var(--font-fallback);
`;

const Input = styled.input<{ $hasError?: boolean }>`
  min-height: 50px;
  padding: 0.85rem 1rem;
  border: 1px solid
    ${props => (props.$hasError ? '#a61d12' : 'rgba(23, 23, 23, 0.32)')};
  border-radius: 12px 12px 12px 4px;
  background: var(--white-pure);
  color: var(--text-primary);
  font-size: 0.9rem;
  font-family: var(--font-primary), var(--font-secondary), var(--font-fallback);
  transition: all 0.3s ease;

  &::placeholder {
    color: var(--gray-medium);
    opacity: 0.65;
  }

  &:focus {
    outline: none;
    border-color: ${props =>
      props.$hasError ? '#dc3545' : 'var(--charcoal-dark)'};
    box-shadow: 0 0 0 3px
      ${props =>
        props.$hasError ? 'rgba(220, 53, 69, 0.1)' : 'rgba(45, 45, 45, 0.1)'};
  }
`;

const TextArea = styled.textarea<{ $hasError?: boolean }>`
  padding: 1rem;
  border: 2px solid
    ${props => (props.$hasError ? '#dc3545' : 'var(--gray-light)')};
  border-radius: 8px;
  background: var(--white-pure);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: var(--font-primary), var(--font-secondary), var(--font-fallback);
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: var(--gray-medium);
    opacity: 0.65;
  }

  &:focus {
    outline: none;
    border-color: ${props =>
      props.$hasError ? '#dc3545' : 'var(--charcoal-dark)'};
    box-shadow: 0 0 0 3px
      ${props =>
        props.$hasError ? 'rgba(220, 53, 69, 0.1)' : 'rgba(45, 45, 45, 0.1)'};
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 0.25rem;
  font-family: var(--font-primary), var(--font-secondary), var(--font-fallback);
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 0.35rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 0.125rem;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 0.7rem;
  color: rgba(23, 23, 23, 0.72);
  line-height: 1.5;
  cursor: pointer;
  font-family: var(--font-primary), var(--font-secondary), var(--font-fallback);

  a {
    color: inherit;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

const SubmitButton = styled.button`
  background: var(--clinic-coral);
  color: var(--clinic-ink);
  padding: 1rem 2rem;
  border: none;
  min-height: 52px;
  border-radius: 15px 15px 15px 5px;
  font-size: 0.74rem;
  font-weight: 800;
  font-family: var(--font-primary), var(--font-secondary), var(--font-fallback);
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.35rem;

  &:disabled {
    background: var(--gray-medium);
    cursor: not-allowed;
    transform: none;
  }

  &:hover:not(:disabled) {
    background: #ff856f;
    transform: translateY(-2px);
    box-shadow: var(--shadow-medium);
  }
`;

const TuMesaYaForm: React.FC<TuMesaYaFormProps> = ({
  onSubmit,
  isSubmitting = false,
  variant = 'hero',
  className,
  compact = false,
  submitLabel = 'Solicitar demo gratuita',
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    restaurant: '',
    additional: '',
    gdpr: false,
  });

  const { trackFormView, trackFormStart, trackFormSubmit, trackFormError } =
    useTracking();
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [hasStarted, setHasStarted] = useState(false);

  // Tracking de vista del formulario
  useEffect(() => {
    trackFormView(variant, 'tuclinicaya-dental');
  }, [trackFormView, variant]);

  const getFieldError = (fieldName: string): string | null => {
    const error = errors.find(err => err.field === fieldName);
    return error ? error.message : null;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    // Tracking de inicio del formulario
    if (!hasStarted && (value || checked)) {
      setHasStarted(true);
      trackFormStart(variant, 'tuclinicaya-dental');
    }

    setFormData((prev: FormData) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors.find(err => err.field === name)) {
      setErrors(prev => prev.filter(err => err.field !== name));
    }
  };

  const handleBlur = (fieldName: string) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      phone: true,
      restaurant: true,
      gdpr: true,
    });

    if (validationErrors.length === 0) {
      // Format data for API
      const formSource =
        variant === 'trial'
          ? 'telemarketing-trial-30min'
          : variant === 'demo'
            ? 'demo-request'
            : variant === 'hero'
              ? 'hero-form'
              : 'final-cta-form';

      // Call parent onSubmit
      const submitResult = await onSubmit(formData);

      if (submitResult !== false) {
        trackFormSubmit(variant, 'tuclinicaya-dental', {
          name: formData.name,
          email: formData.email,
          phone: cleanPhone(formData.phone),
          restaurant: formData.restaurant,
          source: formSource,
          timestamp: new Date().toISOString(),
        });

        setFormData({
          name: '',
          email: '',
          phone: '',
          restaurant: '',
          additional: '',
          gdpr: false,
        });
        setErrors([]);
        setTouched({});
        setHasStarted(false);
      } else {
        trackFormError(variant, 'tuclinicaya-dental', 'submission');
      }
    } else {
      // Track form errors
      validationErrors.forEach(error => {
        trackFormError(variant, 'tuclinicaya-dental', error.field);
      });
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const formattedValue = formatPhone(rawValue);

    setFormData((prev: FormData) => ({
      ...prev,
      phone: formattedValue,
    }));

    // Clear phone error when user types
    if (errors.find(err => err.field === 'phone')) {
      setErrors(prev => prev.filter(err => err.field !== 'phone'));
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit} className={className}>
      <FormGroup>
        <Label htmlFor={`name-${variant || 'default'}`}>
          Nombre y apellidos *
        </Label>
        <Input
          type="text"
          id={`name-${variant || 'default'}`}
          name="name"
          value={formData.name}
          onChange={handleChange}
          onBlur={() => handleBlur('name')}
          placeholder="¿Cómo te llamas?"
          $hasError={!!(touched.name && getFieldError('name'))}
          required
        />
        {touched.name && getFieldError('name') && (
          <ErrorMessage>{getFieldError('name')}</ErrorMessage>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor={`restaurant-${variant || 'default'}`}>
          Clínica dental *
        </Label>
        <Input
          type="text"
          id={`restaurant-${variant || 'default'}`}
          name="restaurant"
          value={formData.restaurant}
          onChange={handleChange}
          onBlur={() => handleBlur('restaurant')}
          placeholder="¿Cómo se llama tu clínica?"
          $hasError={!!(touched.restaurant && getFieldError('restaurant'))}
          required
        />
        {touched.restaurant && getFieldError('restaurant') && (
          <ErrorMessage>{getFieldError('restaurant')}</ErrorMessage>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor={`phone-${variant || 'default'}`}>Teléfono *</Label>
        <Input
          type="tel"
          id={`phone-${variant || 'default'}`}
          name="phone"
          value={formData.phone}
          onChange={handlePhoneChange}
          onBlur={() => handleBlur('phone')}
          placeholder="¿Dónde te podemos llamar?"
          $hasError={!!(touched.phone && getFieldError('phone'))}
          required
        />
        {touched.phone && getFieldError('phone') && (
          <ErrorMessage>{getFieldError('phone')}</ErrorMessage>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor={`email-${variant || 'default'}`}>Email *</Label>
        <Input
          type="email"
          id={`email-${variant || 'default'}`}
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={() => handleBlur('email')}
          placeholder="¿Dónde te podemos escribir?"
          $hasError={!!(touched.email && getFieldError('email'))}
          required
        />
        {touched.email && getFieldError('email') && (
          <ErrorMessage>{getFieldError('email')}</ErrorMessage>
        )}
      </FormGroup>

      {!compact && (
        <FormGroup>
          <Label htmlFor={`additional-${variant || 'default'}`}>
            ¿Deseas añadir algo más? (Opcional)
          </Label>
          <TextArea
            id={`additional-${variant || 'default'}`}
            name="additional"
            value={formData.additional}
            onChange={handleChange}
            placeholder="¿Alguna información adicional?"
            rows={3}
          />
        </FormGroup>
      )}

      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          id={`gdpr-${variant || 'default'}`}
          name="gdpr"
          checked={formData.gdpr}
          onChange={handleChange}
          onBlur={() => handleBlur('gdpr')}
          required
        />
        <CheckboxLabel htmlFor={`gdpr-${variant || 'default'}`}>
          Acepto la{' '}
          <a
            href="/politica-privacidad"
            target="_blank"
            rel="noopener noreferrer"
          >
            política de privacidad
          </a>{' '}
          y el{' '}
          <a
            href="/terminos"
            target="_blank"
            rel="noopener noreferrer"
          >
            tratamiento de datos
          </a>{' '}
          de CitasClinicas.AI *
        </CheckboxLabel>
      </CheckboxContainer>
      {touched.gdpr && getFieldError('gdpr') && (
        <ErrorMessage>{getFieldError('gdpr')}</ErrorMessage>
      )}

      <SubmitButton type="submit" disabled={isSubmitting || !formData.gdpr}>
        {isSubmitting ? 'Enviando...' : submitLabel}
      </SubmitButton>
    </FormContainer>
  );
};

export default TuMesaYaForm;
